import { BusinessObjectBase } from './base';
import { processMessageSchema } from '@schemas/business-objects/process-message';
import type { ProcessMessageProperty } from './process-message-property';
import type { Id } from '../base-types';

export interface ProcessMessageData {
  readonly id: Id;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly properties?: ProcessMessageProperty[];
}

export interface ProcessMessage extends Readonly<ProcessMessageData> {}
type ProcessMessageCreateWithChildrenData = Partial<
  Omit<ProcessMessageData, 'properties'>
> & { properties?: Partial<ProcessMessageProperty>[] };

export class ProcessMessage extends BusinessObjectBase {
  static readonly entityName = 'ProcessMessage' as const;
  static readonly plural = 'process-messages';
  static schema = processMessageSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessMessage[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessMessage(data));
  }

  static async findOne(filter?: any): Promise<ProcessMessage | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessMessage(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessMessage> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessMessage(data);
  }

  static async create(
    data: Omit<ProcessMessageData, 'id'>,
  ): Promise<ProcessMessage> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessMessage(result);
  }

  static async createMany(
    data: Omit<ProcessMessageData, 'id'>[],
  ): Promise<ProcessMessage[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessMessage(item));
  }

  static async createWithChildren(
    data: ProcessMessageCreateWithChildrenData,
  ): Promise<ProcessMessage> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessMessage(result);
  }

  static async createManyWithChildren(
    data: ProcessMessageCreateWithChildrenData[],
  ): Promise<ProcessMessage[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessMessage(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessMessageData>,
  ): Promise<ProcessMessage> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessMessage(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
